import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  CogIcon,
  PaperAirplaneIcon,
  CheckIcon,
  ClockIcon,
  ScaleIcon,
  UserGroupIcon,
  SparklesIcon,
  DocumentDuplicateIcon,
  ShareIcon,
  EnvelopeIcon,
  ArrowDownTrayIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import PremiumUpgradeModal from '../../components/ui/PremiumUpgradeModal';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import FreeHTMLEditor from '../../components/ui/FreeHTMLEditor';
import DocumentPreviewPage from '../../components/ui/DocumentPreviewPage';
import PremiumUpgradeSuccessModal from '../../components/ui/PremiumUpgradeSuccessModal';
import StageVisualization from '../../components/lawsuit/StageVisualization';
import FeedbackModal from '../../components/ui/FeedbackModal';
import axios from 'axios';
import type { Lawsuit as SharedLawsuit } from '@tvia-ai/shared';
import { useLanguageStore } from '../../stores/languageStore';
import { useAuthStore } from '../../stores/authStore';

const LawsuitDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();


  const [editingDocument, setEditingDocument] = useState<SharedLawsuit['documents'][number] | null>(null);
  const [previewingDocument, setPreviewingDocument] = useState<{content: string, originalName: string} | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showPremiumSuccessModal, setShowPremiumSuccessModal] = useState(false);
  const [premiumUpgradeData, setPremiumUpgradeData] = useState<any>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // State to track expanded document version groups
  const [expandedDocGroups, setExpandedDocGroups] = useState<Record<string, boolean>>({});

  const toggleDocGroup = (baseName: string) => {
    setExpandedDocGroups(prev => ({ ...prev, [baseName]: !prev[baseName] }));
  };

  const handleDocumentSaved = (_updatedDocument: any) => {
    // Close the editor and refresh the lawsuit data to show new documents
    setEditingDocument(null);
    // Invalidate and refetch the lawsuit data to get updated documents
    queryClient.invalidateQueries({ queryKey: ['lawsuit', id] });
  };

  // RTL support
  // If you have a language store, use it; otherwise, fallback to i18n dir
  const { i18n } = useTranslation();
  const isRTL = typeof useLanguageStore === 'function' ? useLanguageStore().isRTL : (i18n.dir() === 'rtl');

  // Fetch lawsuit details using React Query
  const {
    data: lawsuit,
    isLoading,
    error,
  } = useQuery<SharedLawsuit | undefined>({
    queryKey: ['lawsuit', id],
    queryFn: async () => {
      if (!id) return undefined;
      const response = await axios.get(`/api/lawsuits/${id}`);
      return response.data.data.lawsuit as SharedLawsuit;
    },
    enabled: !!id,
  });

  // Premium analysis upgrade mutation
  const premiumAnalysisMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/api/lawsuits/${id}/premium-analysis`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lawsuit', id] });
      setPremiumUpgradeData(data.data);
      setShowPremiumSuccessModal(true);
    },
  });

  const handleUpgradeToPremium = async () => {
    await premiumAnalysisMutation.mutateAsync();
    setShowPremiumModal(false);
  };

  const handleOpenPremiumModal = () => {
    setShowPremiumModal(true);
  };

  // Status badge logic
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      analyzing: 'bg-yellow-100 text-yellow-800',
      questions_ready: 'bg-blue-100 text-blue-800',
      analysis_complete: 'bg-blue-100 text-blue-800',
      pending_payment: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      settled: 'bg-green-100 text-green-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800',
      dismissed: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusMap[status] || statusMap.draft}`}>
        {t(`lawsuit.statuses.${status}`, status)}
      </span>
    );
  };

  const handleStrengthenCase = () => {
    navigate(`/lawsuit/${id}/strengthen`);
  };

  const handleStartSubmission = () => {
    navigate(`/lawsuit/${id}/submission`);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: lawsuit?.title || t('lawsuit.lawsuitDetails', 'Lawsuit Details'),
        text: lawsuit?.aiAnalysis?.summary || lawsuit?.userStory || '',
        url: window.location.href,
      }).catch(console.error);
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert(t('lawsuit.linkCopied', 'Link copied to clipboard!'));
      }).catch(() => {
        alert(t('lawsuit.shareError', 'Unable to share. Please copy the URL manually.'));
      });
    }
  };

  const handleFileDownload = async (gcsPath: string, fileName: string) => {
    try {
      // Request a signed URL from the backend with authentication
      const response = await axios.get(`/api/files/signed-url/${encodeURIComponent(gcsPath)}`);
      const { signedUrl } = response.data;
      
      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      alert(t('common.downloadError', 'Failed to download file. Please try again.'));
    }
  };

  // List of all possible document types (should match DocumentGenerationStep)
  const ALL_DOCUMENT_TYPES = ['complaint', 'lawsuit', 'letter', 'contract', 'form'];

  let generatedTypes = new Set<string>();
  let notYetGeneratedTypes: string[] = [];
  let allGenerated = false;
  if (lawsuit) {
    generatedTypes = new Set((lawsuit.documents || []).map((doc: any) => doc.type).filter(Boolean));
    notYetGeneratedTypes = ALL_DOCUMENT_TYPES.filter(type => !generatedTypes.has(type));
    allGenerated = notYetGeneratedTypes.length === 0;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !lawsuit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('errors.lawsuitNotFound', 'Lawsuit not found')}
          </h3>
          <div className="mt-6">
            <Link
              to="/browse-lawsuits"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
            >
              {t('lawsuit.browseLawsuits', 'Browse Lawsuits')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${lawsuit.title || t('lawsuit.lawsuitDetails', 'Lawsuit Details')} - tvia.ai`}</title>
        <meta name="description" content={lawsuit.aiAnalysis?.summary || lawsuit.userStory} />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Main Content - takes up 3/4 of the width */}
            <div className="xl:col-span-3 space-y-8">
              {/* Header */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex-1">
                      <h1 className="text-3xl font-bold text-gray-900 mb-4">
                        {lawsuit.title}
                      </h1>
                      <div className="flex items-center space-x-4 mb-4">
                        {getStatusBadge(lawsuit.status)}
                        <span className="text-sm text-gray-500">
                          {t('lawsuit.category', 'Category')}: {t(`lawsuit.categories.${lawsuit.category}`, lawsuit.category)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {t('lawsuit.country', 'Country')}: {t(`countries.${lawsuit.country}`, lawsuit.country)}
                        </span>
                        {/* Premium Banner - Moved here and made smaller */}
                        {lawsuit.isPremiumAnalysis && (
                          <div className="flex items-center bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-md px-3 py-1">
                            <SparklesIcon className="h-4 w-4 text-yellow-600 mr-2" />
                            <span className="text-sm font-medium text-yellow-800">
                              ✨ {t('lawsuit.premiumAnalysisEnabled', 'Premium AI Analysis')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Analysis - Split into parts */}
              {lawsuit.aiAnalysis && (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-8">
                    <div className="flex items-center mb-6">
                      <SparklesIcon className="h-6 w-6 text-cyan-500 mr-3" />
                      <h2 className="text-2xl font-bold text-gray-900">
                        {t('lawsuit.aiAnalysis', 'AI Analysis')}
                      </h2>
                    </div>

                    {/* Analysis Summary */}
                    <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-6 mb-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {lawsuit.aiAnalysis.caseTitle || t('lawsuit.caseAnalysis', 'Case Analysis')}
                      </h3>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        {lawsuit.aiAnalysis.summary}
                      </p>
                      
                      {/* Key Metrics */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4 text-center">
                          <ChartBarIcon className="h-8 w-8 text-cyan-500 mx-auto mb-2" />
                          <div className="text-2xl font-bold text-gray-900">
                            {lawsuit.aiAnalysis.strengthScore}/100
                          </div>
                          <div className="text-sm text-gray-600">
                            {t('lawsuit.strengthScore', 'Strength Score')}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 text-center">
                          <CheckIcon className="h-8 w-8 text-green-500 mx-auto mb-2" />
                          <div className="text-2xl font-bold text-gray-900">
                            {Math.round((lawsuit.aiAnalysis.confidence || 0) * 100)}%
                          </div>
                          <div className="text-sm text-gray-600">
                            {t('lawsuit.confidence', 'Confidence')}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 text-center">
                          <ClockIcon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                          <div className="text-sm font-semibold text-gray-900">
                            {(() => {
                              if (!lawsuit.aiAnalysis.estimatedTimeline) {
                                return t('common.unknown', 'Unknown');
                              }
                              
                              try {
                                // Always convert to date and format it, regardless of whether it's a string or Date object
                                const date = new Date(lawsuit.aiAnalysis.estimatedTimeline);
                                return date.toLocaleDateString();
                              } catch {
                                return t('common.unknown', 'Unknown');
                              }
                            })()}
                          </div>
                          <div className="text-sm text-gray-600">
                            {t('lawsuit.estimatedTimeline', 'Timeline')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Generated Documents - Enhanced and moved up after AI Analysis */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <DocumentDuplicateIcon className="h-8 w-8 text-blue-600 mr-4" />
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                          {t('lawsuit.generatedDocuments', 'Generated Documents')}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                          {t('lawsuit.generatedDocumentsSubtitle', 'Professional legal documents generated for your case')}
                        </p>
                      </div>
                    </div>
                    {lawsuit.documents && lawsuit.documents.length > 0 && (
                      <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                        <button
                          onClick={() => {
                            // Preview first document (user can navigate through them)
                            if (lawsuit.documents && lawsuit.documents.length > 0) {
                              setPreviewingDocument({
                                content: lawsuit.documents[0].content || '',
                                originalName: `${lawsuit.documents.length} Documents - ${lawsuit.documents[0].originalName}`
                              });
                            }
                          }}
                          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                          {t('lawsuit.previewDocuments', 'Preview All')}
                        </button>
                        {/* Only show the button if not all document types are generated */}
                        {!allGenerated && (
                          <Link
                            to={`/lawsuit/${id}/generate-documents`}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                            {t('lawsuit.generateMoreDocuments', 'Generate More')}
                          </Link>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Documents List or Call-to-Action */}
                  {lawsuit.documents && lawsuit.documents.length > 0 ? (
                    <>
                      {/* Documents Grid */}
                      <div className="space-y-4">
                        {(() => {
                          // Group documents by originalName and type (clean grouping using new properties)
                          const groupedDocs = lawsuit.documents?.reduce((groups: Record<string, SharedLawsuit['documents'][number][]>, doc: any) => {
                            // Use originalName as the grouping key since it's now clean without suffixes
                            const baseName = doc.originalName || doc.filename;
                            
                            if (!groups[baseName]) groups[baseName] = [];
                            groups[baseName].push(doc);
                            return groups;
                          }, {}) || {};

                          // Sort documents within each group by priority: 1) Premium latest, 2) Regular latest, 3) Others by version
                          Object.keys(groupedDocs).forEach(baseName => {
                            groupedDocs[baseName].sort((a: any, b: any) => {
                              const aIsPremium = a.isPremium || false;
                              const bIsPremium = b.isPremium || false;
                              const aVersion = a.version || 1;
                              const bVersion = b.version || 1;
                              
                              // Premium documents get priority
                              if (aIsPremium && !bIsPremium) return -1;
                              if (!aIsPremium && bIsPremium) return 1;
                              
                              // Within same type (premium/regular), sort by version (highest first)
                              return bVersion - aVersion;
                            });
                          });

                          return Object.entries(groupedDocs).map(([baseName, docs]) => {
                            if (docs.length === 1) {
                              // Only one version, render directly
                              const doc = docs[0];
                              const isPremium = doc.isPremium || false;
                              return (
                                <div key={baseName} className={`border border-gray-200 rounded-lg overflow-hidden mb-2 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                                  <div className="flex flex-col sm:flex-row sm:items-center flex-1 p-4 sm:p-6 bg-green-50 hover:bg-opacity-75 transition-colors gap-4">
                                    <div className="flex items-center flex-1 min-w-0">
                                      <div className={`flex flex-col items-center ${isRTL ? 'ml-3' : 'mr-3'}`}>
                                        <DocumentTextIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <p className="text-sm font-medium truncate text-green-900">{doc.originalName}</p>
                                          {isPremium && (
                                            <span className="px-2 py-0.5 rounded bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 text-xs font-medium border border-orange-200">
                                              ✨ {t('common.premium', 'Premium')}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center text-xs text-gray-500 mt-1">
                                          <span>{(doc.size / 1024).toFixed(1)} KB</span>
                                          <span className="mx-2">•</span>
                                          <span>{new Date(doc.uploadedAt).toLocaleString()}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2 w-full sm:w-auto"> 
                                      <button
                                        onClick={() => setPreviewingDocument({ content: doc.content || '', originalName: doc.originalName })}
                                        className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 min-h-[44px] max-h-[44px] transition-all"
                                        title={t('common.previewDownload')}
                                      >
                                        <ArrowDownTrayIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                                        <span className="truncate">{t('common.preview')}</span>
                                      </button>
                                      <button
                                        onClick={() => setEditingDocument(doc)}
                                        className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 min-h-[44px] max-h-[44px] transition-all"
                                      >
                                        <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        <span className="truncate">{t('common.edit')}</span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            // More than one version, show expander
                            const isExpanded = expandedDocGroups[baseName];
                            const hasPremiumVersions = docs.some((doc: any) => doc.isPremium);
                            
                            return (
                              <div key={baseName} className={`border border-gray-200 rounded-lg overflow-hidden mb-2 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                                <button
                                  className="w-full flex items-center justify-between p-4 sm:p-6 bg-gray-100 hover:bg-gray-200 focus:outline-none min-h-[60px] transition-all"
                                  onClick={() => toggleDocGroup(baseName)}
                                >
                                  <div className="flex items-center">
                                    <DocumentTextIcon className="h-5 w-5 text-gray-500 mr-3 flex-shrink-0" />
                                    <span className="font-medium text-gray-900 truncate max-w-xs">{baseName}</span>
                                    <span className={`${isRTL ? 'mr-3' : 'ml-3'} text-xs text-gray-500`}>{docs.length} {t('common.versions')}</span>
                                    {hasPremiumVersions && (
                                      <span className={`${isRTL ? 'mr-2' : 'ml-2'} px-2 py-0.5 rounded bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 text-xs font-medium border border-orange-200`}>
                                        ✨ {t('common.premium', 'Premium')}
                                      </span>
                                    )}
                                  </div>
                                  <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-xs text-blue-600`}>{isExpanded ? t('common.collapse') : t('common.expand')}</span>
                                </button>
                                {/* Only show the latest version if not expanded, else show all */}
                                {(isExpanded ? docs : [docs[0]]).map((doc: any, index: number) => {
                                  const isLatest = index === 0;
                                  const versionNumber = doc.version || 1;
                                  const isPremium = doc.isPremium || false;
                                  return (
                                    <div
                                      key={doc.id}
                                      className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-6 gap-4 ${index > 0 ? 'border-t border-gray-100' : ''} ${isLatest ? 'bg-green-50' : 'bg-gray-50'} hover:bg-opacity-75 transition-colors`}
                                    >
                                      <div className="flex items-center flex-1 min-w-0">
                                        <div className={`flex flex-col items-center ${isRTL ? 'ml-3' : 'mr-3'}`}>
                                          <DocumentTextIcon className={`h-6 w-6 ${isLatest ? 'text-green-600' : 'text-gray-400'} flex-shrink-0`} />
                                          {docs.length > 1 && (
                                            <span className={`text-xs font-medium mt-1 px-1.5 py-0.5 rounded ${isLatest ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>v{versionNumber}</span>
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <p className={`text-sm font-medium truncate ${isLatest ? 'text-green-900' : 'text-gray-700'}`}>{doc.originalName}</p>
                                            {isPremium && (
                                              <span className="px-2 py-0.5 rounded bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 text-xs font-medium border border-orange-200">
                                                ✨ {t('common.premium', 'Premium')}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center text-xs text-gray-500 mt-1">
                                            <span>{(doc.size / 1024).toFixed(1)} KB</span>
                                            <span className="mx-2">•</span>
                                            <span>{new Date(doc.uploadedAt).toLocaleString()}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-2 w-full sm:w-auto"> 
                                        <button
                                          onClick={() => setPreviewingDocument({ content: doc.content || '', originalName: doc.originalName })}
                                          className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 min-h-[44px] max-h-[44px] transition-all"
                                          title={t('common.previewDownload')}
                                        >
                                          <ArrowDownTrayIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                                          <span className="truncate">{t('common.preview')}</span>
                                        </button>
                                        {/* Only allow editing the latest version unless expanded */}
                                        {isLatest && (!isExpanded || index === 0) && (
                                          <button
                                            onClick={() => setEditingDocument(doc)}
                                            className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 min-h-[44px] max-h-[44px] transition-all"
                                          >
                                            <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                            <span className="truncate">{t('common.edit')}</span>
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </>
                  ) : lawsuit.aiAnalysis ? (
                    /* Enhanced Call-to-Action for document generation */
                    <div className="text-center py-12">
                      <div className="max-w-md mx-auto">
                        <div className="flex justify-center mb-6">
                          <div className="relative">
                            <DocumentDuplicateIcon className="h-20 w-20 text-blue-600" />
                            <div className="absolute -top-2 -right-2 bg-blue-600 rounded-full p-2">
                              <SparklesIcon className="h-6 w-6 text-white" />
                            </div>
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">
                          {t('lawsuit.readyToGenerate', 'Ready to Generate Documents')}
                        </h3>
                        <p className="text-gray-600 mb-8 leading-relaxed">
                          {t('lawsuit.generateDocumentsDescription', 'Your case analysis is complete! Generate professional legal documents including complaints, demand letters, settlement agreements, and more.')}
                        </p>
                        <div className="space-y-4">
                          <Link
                            to={`/lawsuit/${id}/generate-documents`}
                            className="w-full inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all transform hover:scale-105"
                          >
                            <DocumentDuplicateIcon className="h-6 w-6 mr-3" />
                            {t('lawsuit.generateDocuments', 'Generate Legal Documents')}
                          </Link>
                          <p className="text-sm text-gray-500">
                            {t('lawsuit.generateDocumentsNote', 'All documents are generated instantly and can be customized to your needs')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* No AI analysis yet */
                    <div className="text-center py-8">
                      <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {t('lawsuit.analysisRequired', 'AI Analysis Required')}
                      </h3>
                      <p className="text-gray-600">
                        {t('lawsuit.analysisRequiredDescription', 'Complete your case analysis first to generate legal documents.')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* AI Analysis - Detailed Analysis (Accused Party, etc.) */}
              {lawsuit.aiAnalysis && (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-8">
                    <div className="flex items-center mb-6">
                      <ScaleIcon className="h-6 w-6 text-cyan-500 mr-3" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t('lawsuit.detailedAnalysis', 'Detailed Analysis')}
                      </h2>
                    </div>

                    {/* Detailed Analysis Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                      <div>
                        <h4 className="text-md font-semibold text-gray-900 mb-3">
                          {t('lawsuit.accusedParty', 'Accused Party')}
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="font-medium text-gray-900">{lawsuit.aiAnalysis.accusedParty?.name}</p>
                          {lawsuit.aiAnalysis.accusedParty?.details && (
                            <p className="text-sm text-gray-600 mt-1">{lawsuit.aiAnalysis.accusedParty.details}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-md font-semibold text-gray-900 mb-3">
                          {t('lawsuit.estimatedClaimAmount', 'Estimated Claim')}
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-2xl font-bold text-gray-900">
                            {lawsuit.aiAnalysis.estimatedClaimAmount?.amount ? 
                              `${lawsuit.aiAnalysis.estimatedClaimAmount.amount.toLocaleString()} ${lawsuit.aiAnalysis.estimatedClaimAmount.currency}` : 
                              t('common.notSpecified', 'Not specified')
                            }
                          </p>
                          {lawsuit.aiAnalysis.estimatedClaimAmount?.justification && (
                            <p className="text-sm text-gray-600 mt-1">{lawsuit.aiAnalysis.estimatedClaimAmount.justification}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Legal Basis */}
                    {lawsuit.aiAnalysis.legalBasis && lawsuit.aiAnalysis.legalBasis.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">
                          {t('lawsuit.legalBasis', 'Legal Basis')}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {lawsuit.aiAnalysis.legalBasis.map((basis: string, idx: number) => (
                            <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                              {basis}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Similar Cases */}
                    {lawsuit.similarCases && lawsuit.similarCases.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">
                          {t('lawsuit.similarCases', 'Similar Cases')}
                        </h4>
                        <div className="space-y-4">
                          {lawsuit.similarCases.map((similarCase: any, idx: number) => (
                            <div key={idx} className="border border-gray-200 rounded-lg p-4">
                              <h5 className="font-medium text-gray-900 mb-2">{similarCase.title}</h5>
                              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-sm text-gray-600">
                                <span><strong>{t('lawsuit.jurisdiction', 'Jurisdiction')}:</strong> {similarCase.jurisdiction}</span>
                                <span><strong>{t('lawsuit.outcome', 'Outcome')}:</strong> {similarCase.outcome}</span>
                                <span><strong>{t('lawsuit.amount', 'Amount')}:</strong> {similarCase.settlementAmount}</span>
                                <span><strong>{t('lawsuit.relevance', 'Relevance')}:</strong> {similarCase.relevance}</span>
                              </div>
                              <p className="text-sm text-gray-700 mt-2">{similarCase.keyFactors}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}



              {/* Generate Documents Call-to-Action (when no documents exist) */}
              {(!lawsuit.documents || lawsuit.documents.length === 0) && lawsuit.aiAnalysis && (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-8">
                    <div className="text-center">
                      <DocumentDuplicateIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {t('lawsuit.noDocumentsYet', 'No Legal Documents Generated Yet')}
                      </h3>
                      <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                        {t('lawsuit.generateDocumentsDescription', 'Generate professional legal documents for your case including complaints, demand letters, settlement agreements, and discovery requests.')}
                      </p>
                      <Link
                        to={`/lawsuit/${id}/generate-documents`}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
                        {t('lawsuit.generateDocuments', 'Generate Documents')}
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* User-Uploaded Files */}
              {lawsuit.files && lawsuit.files.length > 0 && (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-8">
                    <div className="flex items-center mb-6">
                      <DocumentTextIcon className="h-6 w-6 text-gray-500 mr-3" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t('lawsuit.uploadedFiles', 'Uploaded Files')}
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {lawsuit.files?.map((file: any) => (
                        <div key={file.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex items-center flex-1">
                            <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{file.originalName && file.originalName.length > 30 ? file.originalName.slice(0, 27) + '...' : file.originalName}</p>
                              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                              <div className="flex flex-row flex-wrap items-center gap-2 mt-2">
                                <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">
                                  {t('lawsuit.uploaded', 'Uploaded')}
                                </span>
                                {file.gcsPath ? (
                                  <button
                                    onClick={() => file.gcsPath && handleFileDownload(file.gcsPath, file.originalName && file.originalName.length > 30 ? file.originalName.slice(0, 27) + '...' : file.originalName)}
                                    className="text-sm text-cyan-600 hover:text-cyan-700 font-medium"
                                  >
                                    {t('common.download', 'Download')}
                                  </button>
                                ) : (
                                  <span className="text-sm text-gray-400">
                                    {t('common.unavailable', 'Unavailable')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Class Action Information - Moved after documents */}
              {lawsuit.aiAnalysis?.classActionViability && (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-8">
                    <div className="flex items-center mb-6">
                      <UserGroupIcon className="h-6 w-6 text-purple-500 mr-3" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t('lawsuit.classActionInfo', 'Class Action Information')}
                      </h2>
                    </div>

                    {/* Class Action Fit Indicator */}
                    <div className={`rounded-lg p-4 mb-6 ${
                      lawsuit.aiAnalysis.classActionViability.recommended 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center">
                        {lawsuit.aiAnalysis.classActionViability.recommended ? (
                          <CheckIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        ) : (
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
                        )}
                        <div>
                          <h3 className={`text-sm font-medium mb-1 ${
                            lawsuit.aiAnalysis.classActionViability.recommended 
                              ? 'text-green-900' 
                              : 'text-red-900'
                          }`}>
                            {lawsuit.aiAnalysis.classActionViability.recommended 
                              ? t('lawsuit.classActionRecommended', 'Suitable for Class Action')
                              : t('lawsuit.classActionNotRecommended', 'Not Suitable for Class Action')
                            }
                          </h3>
                          <p className={`text-sm ${
                            lawsuit.aiAnalysis.classActionViability.recommended 
                              ? 'text-green-800' 
                              : 'text-red-800'
                          }`}>
                            {lawsuit.aiAnalysis.classActionViability.recommended 
                              ? t('lawsuit.classActionSuitableMessage', 'This case shows good potential for a class action lawsuit.')
                              : t('lawsuit.classActionNotSuitableMessage', 'This case is better pursued as an individual lawsuit.')
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Class Action Reason */}
                    {lawsuit.aiAnalysis.classActionViability.reason && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start">
                          <ScaleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                          <div>
                            <h3 className="text-sm font-medium text-blue-900 mb-1">
                              {t('lawsuit.classActionReason', 'Class Action Reason')}
                            </h3>
                            <p className="text-sm text-blue-800">
                              {lawsuit.aiAnalysis.classActionViability.reason}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Potential Participants for Class Action */}
                    {lawsuit.isClassAction && lawsuit.aiAnalysis.classActionViability.potentialParticipants && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <UserGroupIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                          <div>
                            <h3 className="text-sm font-medium text-green-900 mb-1">
                              {t('lawsuit.potentialParticipants', 'Potential Participants')}
                            </h3>
                            <p className="text-sm text-green-800">
                              {lawsuit.aiAnalysis.classActionViability.potentialParticipants}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* User Info */}
              {lawsuit.userInfo && (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">
                      {t('lawsuit.organizer', 'Case Organizer')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('common.name', 'Name')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lawsuit.userInfo.firstName} {lawsuit.userInfo.lastName}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('common.email', 'Email')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lawsuit.userInfo.email}</dd>
                      </div>
                      {lawsuit.userInfo.phone && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">{t('common.phone', 'Phone')}</dt>
                          <dd className="mt-1 text-sm text-gray-900">{lawsuit.userInfo.phone}</dd>
                        </div>
                      )}
                      {lawsuit.userInfo.address && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">{t('common.address', 'Address')}</dt>
                          <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{lawsuit.userInfo.address}</dd>
                        </div>
                      )}
                      {lawsuit.userInfo.occupation && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">{t('common.occupation', 'Occupation')}</dt>
                          <dd className="mt-1 text-sm text-gray-900">{lawsuit.userInfo.occupation}</dd>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Call to Actions */}
            <div className="xl:col-span-1">
              <div className="sticky top-8 space-y-6">
                {/* Case Actions */}
                <div className="bg-white shadow rounded-lg p-6">

                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('lawsuit.caseActions', 'Case Actions')}
                  </h3>
                  <div className="space-y-3">
                    {!user?.isAdmin ? (
                      <div className="relative w-full mt-2 group">
                        <button
                          className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-400 cursor-not-allowed opacity-70"
                          disabled
                          style={{ position: 'relative' }}
                        >
                          <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                          {t('lawsuit.startSubmission', 'Start Submission')}
                        </button>
                        
                        {/* Coming Soon Banner - moved to edge */}
                        <div className={`absolute top-1 ${isRTL ? 'left-1' : 'right-1'} z-10`}> 
                          <div className={`bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400 text-white text-xs font-bold px-2 py-1 rounded-md shadow-lg transform ${isRTL ? '-rotate-12' : 'rotate-12'} hover:rotate-0 transition-transform duration-200`}>
                            <span>{t('lawsuit.comingSoonBanner', 'Coming soon!')}</span>
                          </div>
                        </div>

                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-20">
                          {t('lawsuit.submitDocumentsComingSoon', 'We will soon add the ability to submit documents')}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={handleStartSubmission}
                        className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
                      >
                        <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                        {t('lawsuit.startSubmission', 'Start Submission')}
                      </button>
                    )}
                    <button
                      onClick={handleStrengthenCase}
                      className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 transition-colors"
                    >
                      <CogIcon className="h-4 w-4 mr-2" />
                      {t('lawsuit.strengthenCase', 'Strengthen Case')}
                    </button>
                    <Link
                      to={`/file-lawsuit?step=verification&lawsuitId=${id}`}
                      className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                      {t('common.edit', 'Edit')}
                    </Link>
                    {!lawsuit.isPremiumAnalysis && (
                      <button
                        onClick={handleOpenPremiumModal}
                        className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 transition-colors"
                      >
                        <SparklesIcon className="h-4 w-4 mr-2" />
                        {t('lawsuit.usePremiumAI', 'Use Premium AI Models')}
                      </button>
                    )}
                    <button
                      onClick={handleShare}
                      className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                    >
                      <ShareIcon className="h-4 w-4 mr-2" />
                      {t('lawsuit.shareCase', 'Share Case')}
                    </button>
                    <button
                      onClick={() => setShowFeedbackModal(true)}
                      className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 transition-colors"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                      {t('feedback.button', 'Give Feedback')}
                    </button>

                  </div>
                </div>

                {/* Case Summary */}
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('lawsuit.caseSummary', 'Case Summary')}
                  </h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('lawsuit.filedOn', 'Filed On')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {lawsuit.createdAt ? new Date(lawsuit.createdAt).toLocaleDateString() : '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('lawsuit.isClassAction', 'Class Action')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {lawsuit.isClassAction ? t('common.yes', 'Yes') : t('common.no', 'No')}
                      </dd>
                    </div>
                    {lawsuit.isClassAction && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">{t('lawsuit.participants', 'Participants')}</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {lawsuit.participants?.length || 0}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Submission Status - Enhanced with Stage Visualization */}
                {(lawsuit.currentSubmissionStage || lawsuit.submissionStages?.length) && (
                  <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('lawsuit.submissionStatus', 'Submission Status')}
                    </h3>
                    <StageVisualization
                      currentStage={lawsuit.currentSubmissionStage}
                      completedStages={lawsuit.submissionStages?.filter(s => s.status === 'completed' || s.status === 'sent').map(s => s.stage) || []}
                      showProgress={true}
                      showNextStage={true}
                    />
                    
                    {lawsuit.accusedPartyEmail && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="text-xs text-gray-500">
                          <div className="flex items-center">
                            <EnvelopeIcon className="h-3 w-3 mr-1" />
                            {lawsuit.accusedPartyEmail}
                            {lawsuit.accusedPartyEmailVerified && (
                              <CheckIcon className="h-3 w-3 text-green-500 ml-1" />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}


              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HTML Document Editor */}
      {editingDocument && (
        <FreeHTMLEditor
          document={editingDocument}
          lawsuitId={id}
          isOpen={!!editingDocument}
          onClose={() => setEditingDocument(null)}
          onSave={handleDocumentSaved}
        />
      )}

      {/* Document Preview Page */}
      {previewingDocument && (
        <DocumentPreviewPage
          document={previewingDocument}
          isOpen={!!previewingDocument}
          onClose={() => setPreviewingDocument(null)}
        />
      )}


      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        onUpgrade={handleUpgradeToPremium}
        isUpgrading={premiumAnalysisMutation.isPending}
      />

      {/* Premium Upgrade Success Modal */}
      <PremiumUpgradeSuccessModal
        isOpen={showPremiumSuccessModal}
        onClose={() => setShowPremiumSuccessModal(false)}
        data={premiumUpgradeData || {}}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        lawsuitId={id || ''}
        lawsuitTitle={lawsuit?.title || ''}
      />
    </>
  );
};

export default LawsuitDetailPage; 